import { useState, useEffect, useRef } from "react";
import type { StructuredFilter } from "@media-viewer/shared";
import { parseSearchQuery } from "../api/client";
import { FilterChips } from "./FilterChips";

interface SearchBarProps {
  filter: StructuredFilter | null;
  setFilter: (f: StructuredFilter | null) => void;
  /** Page-pinned kind (e.g., /videos page). Sent to the parser as pageContext. */
  pinnedKind?: "photo" | "video" | null;
  /** Page-pinned tag (e.g., /tags/:slug page). */
  pinnedTag?: string | null;
}

/**
 * Returns the message shown next to the spinner based on elapsed seconds.
 * Empty string before the first tick so we don't flash text for fast parses.
 */
function busyMessage(elapsedMs: number): string {
  if (elapsedMs < 500) return "";
  const s = Math.floor(elapsedMs / 1000);
  if (s < 3) return "Parsing…";
  if (s < 8) return `Parsing search (${s}s)…`;
  if (s < 20) return `Still thinking (${s}s) — the language model can be slow on cold queries…`;
  return `Almost there (${s}s) — first parse after a model swap takes longer…`;
}

export function SearchBar({ filter, setFilter, pinnedKind, pinnedTag }: SearchBarProps) {
  const [draft, setDraft] = useState(filter?.rawQuery ?? "");
  const [busy, setBusy] = useState(false);
  const [busyStart, setBusyStart] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(filter?.rawQuery ?? "");
  }, [filter?.rawQuery]);

  // Tick elapsed time while busy. Stops as soon as busy flips to false.
  useEffect(() => {
    if (!busy || busyStart === null) {
      setElapsed(0);
      return;
    }
    setElapsed(Date.now() - busyStart);
    const interval = setInterval(() => setElapsed(Date.now() - busyStart), 250);
    return () => clearInterval(interval);
  }, [busy, busyStart]);

  async function submit(): Promise<void> {
    const q = draft.trim();
    setError(null);
    if (q === "") {
      setFilter(null);
      return;
    }
    setBusyStart(Date.now());
    setBusy(true);
    try {
      const { filter: parsed } = await parseSearchQuery(q, {
        kind: pinnedKind ?? null,
        tag: pinnedTag ?? null,
      });
      setFilter(parsed);
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg === "rate_limited" ? "Slow down — try again in a moment." : "Search failed. Try again.");
    } finally {
      setBusy(false);
      setBusyStart(null);
    }
  }

  const status = busy ? busyMessage(elapsed) : "";

  return (
    <div>
      <div className="flex items-center gap-2 px-4 pt-3 text-xs">
        <label className="text-zinc-500">Search:</label>
        <div className="relative flex-1">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submit();
              else if (e.key === "Escape") {
                setDraft("");
                setFilter(null);
              }
            }}
            disabled={busy}
            placeholder={`Search — try "videos 1-5 min with red but not blue"`}
            className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 pr-7 font-mono text-zinc-200 disabled:opacity-60"
          />
          {busy && (
            <span
              aria-label="Parsing your search"
              className="absolute right-2 top-1.5 inline-block h-3 w-3 animate-spin rounded-full border-2 border-zinc-500 border-t-transparent"
            />
          )}
        </div>
        {status && <span className="text-zinc-400" aria-live="polite">{status}</span>}
        {error && <span className="text-rose-400">{error}</span>}
      </div>
      <FilterChips filter={filter} setFilter={setFilter} pinnedKind={pinnedKind} />
    </div>
  );
}
